import { useState } from "react";
import { Gift, Copy, Share2, Users, Check, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const ReferralSection = ({ customer }) => {
  const [copied, setCopied] = useState(false);
  
  const referralCode = customer?.referral_code || null;
  const referralCount = customer?.referral_count || 0;
  const referrals = customer?.referrals || [];
  const maxReferrals = 5;
  const progress = (referralCount / maxReferrals) * 100;
  
  const copyCode = () => {
    if (!referralCode) return;
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    toast.success("Code copié !");
    setTimeout(() => setCopied(false), 2000);
  };
  
  const shareViaWhatsApp = () => {
    if (!referralCode) return;
    const text = `🌸 Je te recommande Pressing Sakura ! Utilise mon code ${referralCode} à l'inscription pour recevoir 5€ offerts. https://www.pressing-sakura.fr/`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };
  
  const shareViaSMS = () => {
    if (!referralCode) return;
    const text = `Je te recommande Pressing Sakura ! Utilise mon code ${referralCode} à l'inscription pour recevoir 5€ offerts. https://www.pressing-sakura.fr/`;
    window.open(`sms:?body=${encodeURIComponent(text)}`, "_blank");
  };
  
  const shareViaEmail = () => {
    if (!referralCode) return;
    const subject = "5€ offerts chez Pressing Sakura !";
    const body = `Salut !\n\nJe te recommande Pressing Sakura, un super service de pressing à domicile.\n\nUtilise mon code parrainage : ${referralCode}\n\nTu recevras 5€ offerts sur ton premier pressing !\n\nInscris-toi ici : https://www.pressing-sakura.fr/\n\nÀ bientôt !`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
  };
  
  return (
    <Card className="border-pink-200 bg-gradient-to-br from-pink-50 to-white" data-testid="referral-section">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-pink-700" data-testid="referral-title">
          <Gift className="w-5 h-5" />
          Programme Parrainage 🌸
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Referral Code */}
        <div className="bg-white rounded-lg p-4 border border-pink-100">
          <p className="text-sm text-slate-500 mb-2">Ton code parrainage</p>
          {referralCode ? (
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-pink-100 text-pink-700 px-4 py-2 rounded-lg font-mono text-lg font-bold text-center" data-testid="referral-code">
                {referralCode}
              </code>
              <Button
                variant="outline"
                size="icon"
                onClick={copyCode}
                className="border-pink-200 hover:bg-pink-50"
                data-testid="copy-referral-code-btn"
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-pink-600" />}
              </Button>
            </div>
          ) : (
            <div className="bg-amber-50 text-amber-700 px-4 py-3 rounded-lg text-center" data-testid="referral-code-pending">
              <p className="font-medium">⏳ Code en cours de génération...</p>
              <p className="text-sm mt-1">Ton code parrainage sera disponible très bientôt !</p>
            </div>
          )}
        </div>
        
        {/* Share Buttons - only if code exists */}
        {referralCode && (
          <div className="flex gap-2" data-testid="share-buttons">
            <Button
              variant="outline"
              size="sm"
              onClick={shareViaWhatsApp}
              className="flex-1 border-green-200 text-green-600 hover:bg-green-50"
              data-testid="share-whatsapp-btn"
            >
              <Share2 className="w-4 h-4 mr-1" />
              WhatsApp
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={shareViaSMS}
              className="flex-1 border-blue-200 text-blue-600 hover:bg-blue-50"
              data-testid="share-sms-btn"
            >
              <Share2 className="w-4 h-4 mr-1" />
              SMS
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={shareViaEmail}
              className="flex-1 border-purple-200 text-purple-600 hover:bg-purple-50"
              data-testid="share-email-btn"
            >
              <Share2 className="w-4 h-4 mr-1" />
              Email
            </Button>
          </div>
        )}
        
        {/* Progress - only if code exists */}
        {referralCode && (
          <div className="bg-white rounded-lg p-4 border border-pink-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Progression</span>
              <span className="text-sm font-bold text-pink-600">{referralCount}/{maxReferrals}</span>
            </div>
            <Progress value={progress} className="h-2 bg-pink-100" />
            {referralCount >= maxReferrals ? (
              <div className="flex items-center gap-2 mt-2 text-green-600">
                <Award className="w-4 h-4" />
                <span className="text-sm font-medium">Objectif atteint ! Tirage Sakura débloqué 🎉</span>
              </div>
            ) : (
              <p className="text-xs text-slate-500 mt-2">
                Encore {maxReferrals - referralCount} parrainage(s) pour débloquer un tirage Sakura bonus !
              </p>
            )}
          </div>
        )}
        
        {/* Referrals List - only if code exists and has referrals */}
        {referralCode && referrals.length > 0 && (
          <div className="bg-white rounded-lg p-4 border border-pink-100">
            <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Mes filleuls ({referrals.length})
            </p>
            <div className="space-y-2">
              {referrals.map((ref, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                  <span className="text-slate-600">{ref.filleul_name}</span>
                  <Badge className={ref.status === "validated" ? "bg-green-500" : "bg-amber-500"}>
                    {ref.status === "validated" ? "Validé (+5€)" : "En attente"}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Rules */}
        <div className="bg-pink-50 rounded-lg p-4 text-sm text-pink-700">
          <p className="font-semibold mb-2">📋 Règles du programme</p>
          <ul className="space-y-1 text-xs">
            <li>• <strong>Vous parrainez :</strong> gagnez 5€ par filleul (crédités après sa 1ère livraison)</li>
            <li>• <strong>Votre filleul :</strong> reçoit 5€ dès son inscription</li>
            <li>• Maximum 5 parrainages par client</li>
            <li>• 🎁 Bonus : 1 tirage Sakura offert après 5 parrainages !</li>
            <li className="text-pink-500">• ⚠️ Non cumulable avec un autre code promo</li>
            <li className="text-pink-500">• ⚠️ Adresses postales différentes obligatoires</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReferralSection;
